import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as ServiceIcon,
} from '@mui/icons-material';
import { format, addDays, isToday, isTomorrow } from 'date-fns';
import { schedulingApi, serviceMappingApi, appointmentsApi } from '../../services/api';
import { dayStrip, StripAppointment, StripRow } from './dayStrip';
import { toPracticeWallClock } from '../../utils/practiceTime';
import { formatClockString } from '../../utils/dateTime';

interface QuickScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onScheduled: (appointment: any) => void;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  /** The clinic's clock, 'HH:MM' — what the dialog shows (quickSlotLabel). */
  time?: string;
  timeDisplay?: string;
  available: boolean;
}

interface Patient {
  id: string;
  name: string;
  phone: string;
  email?: string;
  internalId?: string;
  spruceContactId?: string;
}

interface ServiceProviderMapping {
  id: string;
  serviceId: string;
  specialistId: string;
  service: {
    id: string;
    name: string;
    duration: number;
  };
  specialist: {
    id: string;
    name: string;
    workingHours?: Record<string, { start: string; end: string }[]>;
  };
}

const QuickScheduleModal: React.FC<QuickScheduleModalProps> = ({
  open,
  onClose,
  onScheduled,
}) => {
  // Step state
  const [step, setStep] = useState<'patient' | 'service' | 'slot'>('patient');
  
  // Data states
  const [mappings, setMappings] = useState<ServiceProviderMapping[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  // Who is in the provider's day, in the clinic's clock, for the strip.
  const [dayAppointments, setDayAppointments] = useState<StripAppointment[]>([]);
  
  // Selection states
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedMapping, setSelectedMapping] = useState<ServiceProviderMapping | null>(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [notes, setNotes] = useState('');
  
  // Loading states
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch service-provider mappings on mount
  useEffect(() => {
    const fetchMappings = async () => {
      if (!open) return;
      
      setIsLoadingMappings(true);
      try {
        const response = await serviceMappingApi.getAll();
        setMappings(response.data?.mappings || []);
      } catch (err) {
        console.error('Failed to fetch mappings:', err);
        setError('Failed to load services');
      } finally {
        setIsLoadingMappings(false);
      }
    };
    
    fetchMappings();
  }, [open]);

  // Search patients
  useEffect(() => {
    const searchPatients = async () => {
      if (patientSearch.length < 2) {
        setPatientResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const response = await schedulingApi.searchPatients(patientSearch);
        setPatientResults(response.data?.data || []);
      } catch (err) {
        console.error('Patient search failed:', err);
      } finally {
        setIsSearching(false);
      }
    };
    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [patientSearch]);

  // Fetch time slots when mapping and date are selected
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedMapping || !selectedDate) return;
      
      setIsLoadingSlots(true);
      setError(null);
      setSelectedSlot(null);
      
      try {
        const response = await schedulingApi.getSlots(
          selectedMapping.service.id,
          selectedMapping.specialist.id,
          selectedDate
        );
        // Backend returns { success: true, data: { slots: [...], ... } }
        const responseData = response.data?.data || response.data || {};
        const slots = responseData.slots || responseData || [];
        setTimeSlots(Array.isArray(slots) ? slots : []);
        // The day's appointments for this provider, so a booked half hour can
        // say who. Best effort: without them the strip still says "Booked".
        try {
          const dayAfter = new Date(`${selectedDate}T12:00:00`); dayAfter.setDate(dayAfter.getDate() + 2);
          const pad = (n: number) => String(n).padStart(2, '0');
          const endDate = `${dayAfter.getFullYear()}-${pad(dayAfter.getMonth() + 1)}-${pad(dayAfter.getDate())}`;
          const apptRes = await appointmentsApi.getAppointments({ startDate: selectedDate, endDate });
          const rows: any[] = apptRes.data?.data || apptRes.data?.appointments || [];
          const providerName = selectedMapping.specialist.name;
          const providerId = selectedMapping.specialist.id;
          setDayAppointments(rows
            .filter((a) => !['cancelled', 'no_show'].includes(String(a.status || '').toLowerCase()))
            .filter((a) => (a.specialistId && a.specialistId === providerId) || a.provider === providerName)
            .map((a) => {
              const startWall = toPracticeWallClock(a.startTime);
              const endWall = a.endTime
                ? toPracticeWallClock(a.endTime)
                : toPracticeWallClock(new Date(new Date(a.startTime).getTime() + (Number(a.duration) || 30) * 60000));
              return { date: startWall.slice(0, 10), start: startWall.slice(11, 16), end: endWall.slice(11, 16), patientName: a.patientName };
            })
            .filter((a) => a.date === selectedDate));
        } catch (apptErr) {
          setDayAppointments([]);
        }
      } catch (err) {
        console.error('Failed to fetch slots:', err);
        setError('Failed to load available time slots');
        setTimeSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };
    
    if (step === 'slot') {
      fetchSlots();
    }
  }, [selectedMapping, selectedDate, step]);

  // Get available dates (next 14 days) with working day info
  const availableDates = useMemo(() => {
    const dates: { value: string; label: string; dayOfWeek: string; isWorking: boolean }[] = [];
    for (let i = 0; i < 14; i++) {
      const date = addDays(new Date(), i);
      const dayOfWeek = format(date, 'EEEE').toLowerCase();
      
      // Check if provider is working on this day
      let isWorking = true;
      if (selectedMapping?.specialist?.workingHours) {
        const workingHours = selectedMapping.specialist.workingHours[dayOfWeek] || [];
        isWorking = workingHours.length > 0;
      }
      
      dates.push({
        value: format(date, 'yyyy-MM-dd'),
        label: isToday(date) 
          ? 'Today' 
          : isTomorrow(date) 
            ? 'Tomorrow' 
            : format(date, 'EEE, MMM d'),
        dayOfWeek,
        isWorking,
      });
    }
    return dates;
  }, [selectedMapping]);

  // Reset state on close
  const handleClose = () => {
    setStep('patient');
    setPatientSearch('');
    setPatientResults([]);
    setSelectedPatient(null);
    setSelectedMapping(null);
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
    setSelectedSlot(null);
    setNotes('');
    setError(null);
    onClose();
  };

  // Handle patient selection
  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setStep('service');
  };

  // Handle mapping (service+provider) selection
  const handleMappingSelect = (mapping: ServiceProviderMapping) => {
    setSelectedMapping(mapping);
    setStep('slot');
  };

  // Handle scheduling
  const handleSchedule = async () => {
    if (!selectedPatient || !selectedMapping || !selectedSlot) return;

    setIsScheduling(true);
    setError(null);
    try {
      const appointmentData = {
        patientName: selectedPatient.name,
        patientPhone: selectedPatient.phone,
        patientEmail: selectedPatient.email,
        internalPatientId: selectedPatient.internalId,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        serviceId: selectedMapping.service.id,
        specialistId: selectedMapping.specialist.id,
        provider: selectedMapping.specialist.name,
        appointmentType: selectedMapping.service.name.toLowerCase().replace(/\s+/g, '_'),
        title: `${selectedMapping.service.name} - ${selectedPatient.name}`,
        duration: selectedMapping.service.duration,
        presentingComplaints: notes, // Maps to Chief Complaint column
        status: 'scheduled',
      };
      
      const response = await schedulingApi.createAppointment(appointmentData);
      const saved = response.data?.data || response.data?.appointment || appointmentData;
      onScheduled(saved);
      handleClose();
    } catch (err) {
      console.error('Failed to schedule:', err);
      setError('Failed to create appointment. Please try again.');
    } finally {
      setIsScheduling(false);
    }
  };


  const availableSlots = timeSlots.filter(slot => slot.available);
  const stripRows: StripRow[] = dayStrip(timeSlots as any, dayAppointments);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: 500 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6">Quick Schedule</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Progress indicator */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip 
            label="1. Patient" 
            color={step === 'patient' ? 'primary' : selectedPatient ? 'success' : 'default'}
            onClick={() => setStep('patient')}
            sx={{ cursor: 'pointer' }}
          />
          <Chip 
            label="2. Service & Provider" 
            color={step === 'service' ? 'primary' : selectedMapping ? 'success' : 'default'}
            onClick={() => selectedPatient && setStep('service')}
            disabled={!selectedPatient}
            sx={{ cursor: selectedPatient ? 'pointer' : 'default' }}
          />
          <Chip 
            label="3. Time Slot" 
            color={step === 'slot' ? 'primary' : selectedSlot ? 'success' : 'default'}
            onClick={() => selectedMapping && setStep('slot')}
            disabled={!selectedMapping}
            sx={{ cursor: selectedMapping ? 'pointer' : 'default' }}
          />
        </Box>

        {/* Step 1: Patient Search */}
        {step === 'patient' && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Search for Patient
            </Typography>
            <TextField
              fullWidth
              placeholder="Search by name, phone, or ID..."
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
              autoFocus
              InputProps={{
                endAdornment: isSearching && <CircularProgress size={20} />,
              }}
            />
            
            {patientResults.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                <List>
                  {patientResults.map((patient, idx) => (
                    <React.Fragment key={patient.id}>
                      <ListItemButton onClick={() => handlePatientSelect(patient)}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={patient.name}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                              <Typography component="span" variant="caption">
                                {patient.phone}
                              </Typography>
                              {patient.internalId && (
                                <Chip 
                                  label={patient.internalId} 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                      {idx < patientResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}

            {selectedPatient && (
              <Paper 
                variant="outlined" 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'success.50',
                  borderColor: 'success.main',
                }}
              >
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                  Selected Patient
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedPatient.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPatient.phone}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {/* Step 2: Combined Service & Provider Selection */}
        {step === 'service' && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Select Service & Provider
            </Typography>
            
            {isLoadingMappings ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : mappings.length === 0 ? (
              <Alert severity="warning">
                No services configured. Please add service-provider mappings in Admin settings.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {mappings.map((mapping) => (
                  <Paper
                    key={mapping.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      bgcolor: selectedMapping?.id === mapping.id ? 'primary.50' : 'transparent',
                      borderColor: selectedMapping?.id === mapping.id ? 'primary.main' : 'divider',
                      borderWidth: selectedMapping?.id === mapping.id ? 2 : 1,
                      '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                      transition: 'all 0.2s',
                    }}
                    onClick={() => handleMappingSelect(mapping)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <ServiceIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {mapping.service.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          with {mapping.specialist.name} • {mapping.service.duration} min
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${mapping.service.duration} min`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Step 3: Time Slot Selection */}
        {step === 'slot' && (
          <Box>
            {/* Show selected service+provider */}
            {selectedMapping && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Booking:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedMapping.service.name} with {selectedMapping.specialist.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {selectedMapping.service.duration} minutes
                </Typography>
              </Paper>
            )}

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Select Date
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 350, overflow: 'auto' }}>
                  {availableDates.map((date) => {
                    const isWorking = date.isWorking;
                    return (
                      <Paper
                        key={date.value}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          cursor: isWorking ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          bgcolor: selectedDate === date.value ? 'primary.50' : 'transparent',
                          borderColor: selectedDate === date.value ? 'primary.main' : 'divider',
                          opacity: isWorking ? 1 : 0.5,
                          '&:hover': isWorking ? { borderColor: 'primary.light' } : {},
                        }}
                        onClick={() => isWorking && setSelectedDate(date.value)}
                      >
                        <CalendarIcon fontSize="small" color={isWorking ? 'action' : 'disabled'} />
                        <Typography variant="body2">{date.label}</Typography>
                        {!isWorking && (
                          <Chip label="Off" size="small" sx={{ ml: 'auto', height: 20 }} />
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Available Time Slots
                </Typography>
                
                {isLoadingSlots ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : stripRows.length === 0 ? (
                  <Alert severity="info">
                    No working hours for this provider on this date. Please select another date.
                  </Alert>
                ) : (
                  <Box>
                    {availableSlots.length === 0 && (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        No open time long enough for this visit on this date — here is the day.
                      </Alert>
                    )}
                    {/* The provider's day as one strip: open rows to tap, the
                        rest named. Two columns when there is room, one on a
                        phone; a row never splits across columns. */}
                    <Box sx={{ columnCount: { xs: 1, md: 2 }, columnGap: 2 }}>
                      {stripRows.map((row) => {
                        const open = row.state === 'open' && row.slot;
                        const selected = open && selectedSlot?.startTime === (row.slot as any).startTime;
                        const tone = row.state === 'booked' ? { bgcolor: 'grey.200', color: 'text.primary' }
                          : row.state === 'held' ? { bgcolor: 'warning.50', color: 'warning.dark', border: '1px dashed', borderColor: 'warning.main' }
                          : row.state === 'off' ? { bgcolor: 'transparent', color: 'text.disabled' }
                          : row.state === 'open' ? (selected ? { bgcolor: 'primary.main', color: 'primary.contrastText' } : { bgcolor: 'background.paper', color: 'primary.main', border: '1px solid', borderColor: 'primary.main' })
                          : { bgcolor: 'grey.100', color: 'text.disabled' };
                        return (
                          <Box
                            key={`${row.start}-${row.state}`}
                            role={open ? 'button' : undefined}
                            tabIndex={open ? 0 : undefined}
                            onClick={open ? () => setSelectedSlot(row.slot as any) : undefined}
                            onKeyDown={open ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedSlot(row.slot as any); } } : undefined}
                            sx={{
                              breakInside: 'avoid', display: 'flex', alignItems: 'center', gap: 1.5,
                              px: 1.5, py: row.state === 'off' ? 0.5 : 1, mb: 0.75, borderRadius: 1.5,
                              minHeight: row.state === 'off' ? 28 : 44, cursor: open ? 'pointer' : 'default',
                              fontSize: 14, ...tone,
                              '&:hover': open && !selected ? { bgcolor: 'primary.50' } : undefined,
                              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                            }}
                          >
                            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 112, fontWeight: open ? 700 : 500, opacity: row.state === 'off' ? 0.8 : 1 }}>
                              {row.state === 'open' ? row.label : `${formatClockString(row.start)}–${formatClockString(row.end)}`}
                            </Box>
                            <Box component="span" sx={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
                              {row.state === 'open' ? (selected ? 'Selected' : 'Open — tap to choose') : row.label}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {selectedSlot && (
                  <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Appointment Notes (optional)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Add any notes for this appointment..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Paper>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step !== 'patient' && (
          <Button 
            onClick={() => setStep(step === 'slot' ? 'service' : 'patient')}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose}>Cancel</Button>
        {step === 'slot' && (
          <Button
            variant="contained"
            onClick={handleSchedule}
            disabled={!selectedSlot || isScheduling}
            startIcon={isScheduling && <CircularProgress size={16} color="inherit" />}
          >
            {isScheduling ? 'Scheduling...' : 'Schedule Appointment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuickScheduleModal;
